import { Box, Divider, Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { Menu, usePermissions } from 'react-admin';
import { FaComments, FaCogs, FaUserFriends, FaSortAmountDown, FaTrophy, FaHandshake, FaWalking, FaUserShield, FaTags, FaGem, FaUsers, FaImage } from 'react-icons/fa';
import { ROLE } from './providers/auth.types';

export const MyMenu = () => {
	const [menuTab, setMenuTab] = useState<'main' | 'global'>('main');
	const { permissions } = usePermissions<ROLE>();

	// GH-724 — 'expert-manager' (Yulia): experts, therapists, expert categories and support circles only.
	// Menu.Item links are not role-filtered by react-admin (only Menu.ResourceItem is), so this role
	// gets its own short menu instead of a list of links that would all 403.
	// GH-735 — renamed "תמיכה טכנית": + lectures & workshops, service bank, VOD and VOD categories.
	// GH-737 — + onboarding notes (company + note text only).
	if (permissions === 'expert-manager') {
		return (
			<Menu>
				<Menu.ResourceItem name="instructor/allin/experts" />
				<Menu.ResourceItem name="instructor/allin/safe-space" />
				<Menu.ResourceItem name="expert-ranking" />
				<Menu.ResourceItem name="instructor-category" />
				<Menu.Item to="/support-circles" primaryText="מעגלי תמיכה" leftIcon={<FaUsers />} />
				<Menu.ResourceItem name="member-notes" />
				<Divider />
				<Menu.ResourceItem name="meeting" />
				<Menu.ResourceItem name="service" />
				<Menu.ResourceItem name="vod" />
				<Menu.ResourceItem name="vod/categories" />
			</Menu>
		);
	}

	// GH-735 — 'business-admin' ("מנהלת עסקית"): companies (incl. the per-company budget tab — NOT the
	// cross-company "מימושי תקציב חכם" page), members, experts,
	// lectures & workshops and the service bank. Own menu for the same reason as above.
	if (permissions === 'business-admin') {
		return (
			<Menu>
				<Menu.DashboardItem />
				<Menu.ResourceItem name="company" />
				<Menu.ResourceItem name="member" />
				<Divider />
				<Menu.ResourceItem name="instructor/allin/experts" />
				<Menu.ResourceItem name="instructor/allin/safe-space" />
				<Menu.ResourceItem name="expert-ranking" />
				<Divider />
				<Menu.ResourceItem name="meeting" />
				<Menu.ResourceItem name="service" />
				<Divider />
				{/* GH-810 — the partners (vendors) page is the business manager's */}
				<Menu.Item to="/partners" primaryText="שותפים" leftIcon={<FaHandshake />} />
			</Menu>
		);
	}

	// פריטים שהוסרו מהתפריט (08/2026) אך עדיין רשומים כ-resources ונגישים ב-URL ישיר:
	// dating-events, events-coupons, article, blog, lifestyle-area/all, section,
	// invitation (מנויי לידה), /event-invites (הזמנות לאירועים)
	return (
		<Menu>
			<Box sx={{ px: 1, pb: 1 }}>
				<Tabs
					value={menuTab}
					onChange={(_, value: 'main' | 'global') => setMenuTab(value)}
					variant="fullWidth"
					sx={{ minHeight: 40 }}
				>
					<Tab value="main" label="ראשי" sx={{ minHeight: 40 }} />
					<Tab value="global" label="גלובלי" sx={{ minHeight: 40 }} />
				</Tabs>
			</Box>
			{menuTab === 'main' ? (
				<>
					<Menu.DashboardItem />
					<Menu.Item
						to="/subsidy-offers"
						primaryText="מימושי תקציב חכם"
						leftIcon={<FaGem />}
					/>
					<Menu.ResourceItem name="member" />
					<Menu.ResourceItem name="company" />
					<Menu.ResourceItem name="activity-logs" />
					<Menu.ResourceItem name="anonymous-feedback" />
					<Menu.ResourceItem name="member-notes" />
					<Menu.ResourceItem name="interventions" />
					<Divider />
					<Menu.ResourceItem name="vod" />
					<Menu.ResourceItem name="podcasts" />
					<Menu.Item to="/vod/categories?type=podcast" primaryText="קטגוריות פודקאסט" />
					<Menu.ResourceItem name="media-links" />
					<Menu.ResourceItem name="member/get-searches" />
					<Menu.ResourceItem name="ai-program" />
					<Menu.ResourceItem name="ai-program-prize" />
					<Menu.ResourceItem name="tag/all" />
					<Menu.ResourceItem name="vod/categories" />
					<Divider />
					{/* <Menu.ResourceItem name="lecture" />
					<Menu.ResourceItem name="lecture/category" />
					<Menu.ResourceItem name="learning/course" /> */}
					<Divider />
					<Menu.ResourceItem name="service" />
					<Menu.ResourceItem name="meeting" />
					<Menu.ResourceItem name="review" />
					<Divider />
					<Menu.ResourceItem name="proactive/item" />
					<Menu.ResourceItem name="prize" />
					<Menu.Item
						to="/prize-order"
						primaryText="סידור פרסים"
						leftIcon={<FaSortAmountDown />}
					/>
					<Menu.ResourceItem name="coupon" />
					<Menu.ResourceItem name="survey/question" />
					<Divider />
					<Menu.ResourceItem name="instructor/allin/experts" />
					<Menu.ResourceItem name="instructor/allin/safe-space" />
					<Menu.ResourceItem name="expert-ranking" />
					<Menu.ResourceItem name="instructor-category" />
					<Menu.ResourceItem name="instructor/private_services/sessions" />
					<Menu.ResourceItem name="private-service-packages" />
					<Menu.ResourceItem name="instructor/sessions/stats/by_host" />
					<Menu.Item
						to="/instructor/expert-chat/messages"
						primaryText="שאלות צ׳אט מומחים"
						leftIcon={<FaComments />}
					/>
					<Menu.Item
						to="/family-invites"
						primaryText="הזמנות בני משפחה"
						leftIcon={<FaUserFriends />}
					/>
					<Menu.Item
						to="/system-functions"
						primaryText="פונקציות מערכת"
						leftIcon={<FaCogs />}
					/>
					<Menu.Item
						to="/support-circles"
						primaryText="מעגלי תמיכה"
						leftIcon={<FaUsers />}
					/>
					<Menu.Item
						to="/step-competition"
						primaryText="תחרות הצעדים"
						leftIcon={<FaTrophy />}
					/>
					<Menu.Item
						to="/step-challenge"
						primaryText="אתגר צעדים אוטונומי"
						leftIcon={<FaWalking />}
					/>
					<Menu.Item
						to="/identity-verification"
						primaryText="אימות זהות"
						leftIcon={<FaUserShield />}
					/>
					<Menu.Item
						to="/releases"
						primaryText="גרסאות — מה השתנה"
						leftIcon={<FaTags />}
					/>
					<Menu.Item
						to="/partners"
						primaryText="שותפים"
						leftIcon={<FaHandshake />}
					/>
					<Divider />
					<Menu.ResourceItem name="setting/trial" />
					<Menu.ResourceItem name="member/email-group" />
					<Menu.ResourceItem name="setting" />
					{/* GH-766 — general banner + lottery popup: admin + superadmin (server: GET/PUT setting/banner, onlyAdmin) */}
					{(permissions === 'admin' || permissions === 'superadmin') && (
						<Menu.Item to="/main-banner" primaryText="באנר ראשי" leftIcon={<FaImage />} />
					)}
					<Menu.ResourceItem name="member-leads" />
					<Menu.ResourceItem name="admin/permissions" />
				</>
			) : (
				<>
					<Menu.Item to="/instructor/allin/experts?is_global=true" primaryText="מומחים" />
					<Menu.Item to="/instructor/allin/safe-space?is_global=true" primaryText="מטפלים" />
					<Menu.Item to="/instructor-category?is_global=true" primaryText="קטגוריות מומחים" />
					<Menu.Item to="/service?is_global=true" primaryText="שירותים" />
					<Menu.Item to="/meeting?is_global=true" primaryText="הרצאות וסדנאות" />
					<Menu.Item to="/vod?is_global=true" primaryText="VOD" />
					<Menu.Item to="/vod/categories?is_global=true" primaryText="קטגוריות VOD" />
					<Menu.Item to="/podcasts?is_global=true" primaryText="פודקאסטים" />
					<Menu.Item to="/vod/categories?type=podcast&is_global=true" primaryText="קטגוריות פודקאסט" />
					<Menu.Item to="/proactive/item?is_global=true" primaryText="בוסטים" />
					<Menu.Item to="/prize?is_global=true" primaryText="פרסים" />
				</>
			)}
		</Menu>
	);
};
